import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/* Read-side over Engage's local caches (refreshed by jt-sync). Populates the
 * campaign builder's Enrollment-step trigger dropdowns. No live JT calls. */

export interface JobStageOption {
  /** Stored verbatim in trigger_config.match.to_stage / from_stage. */
  value: string;
  label: string;
}

export interface PipelineStageOption {
  stage_id: string;
  name: string;
  order: number;
  type: string | null;
}

export interface PipelineOption {
  pipeline_id: string;
  name: string;
  stages: PipelineStageOption[];
}

export interface BookingLinkOption {
  /** The JobTetris event type id, stored in trigger_config.match.event_type_ids. */
  value: string;
  label: string;
  owner_name: string;
}

/** Every user's JobTetris booking links, for the meeting-booked trigger (JT1-1920). */
export function useBookingLinkOptions() {
  return useQuery<{ data: BookingLinkOption[] }>({
    queryKey: ['trigger-options', 'booking-links'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: BookingLinkOption[] }>('/trigger-options/booking-links');
      return data;
    },
    staleTime: 60_000,
  });
}

/** Fixed JT ApplicationStage list — same for every job. */
export function useJobStages() {
  return useQuery<{ data: JobStageOption[] }>({
    queryKey: ['trigger-options', 'job-stages'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: JobStageOption[] }>('/trigger-options/job-stages');
      return data;
    },
    // Static — never goes stale within a session.
    staleTime: Infinity,
  });
}

/** Deal pipelines + their stages, from the cache. Empty until jt-sync has run. */
export function usePipelines() {
  return useQuery<{ data: PipelineOption[] }>({
    queryKey: ['trigger-options', 'pipelines'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: PipelineOption[] }>('/trigger-options/pipelines');
      return data;
    },
    staleTime: 60_000,
  });
}
